import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Button, Text } from 'rebass';
import { MdUndo, MdRedo } from 'react-icons/md';
import PageLockToggle from './PageLockToggle';

// The spread's shared editing controls: each page's own lock toggle flanking
// a centred Undo / Redo / Save changes cluster. Rendered above the book in
// FlipbookPanel — kept separate from the generic multi-step Header (shared
// by the cover/stories/chapters steps) so this book-layout-specific control
// set can't affect those other steps.
const BookEditorTopBar = ({
  leftLock,
  rightLock,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  canSave,
  isSaving,
  onSaveChanges,
  width,
  checkMarkSuccess,
  setCheckMarkSuccess,
}) => (
  <Flex
    // Lets MediaSelectionOverlay's "click outside deselects" guard recognize
    // these controls as acting on/near the current selection (Undo/Redo
    // must never clear it; Save Changes handles clearing it itself on
    // success) rather than a click away from it.
    data-preserve-selection
    alignItems="center"
    justifyContent="space-between"
    mb={2}
    sx={{
      padding: '10px 0px',
      position: 'fixed',
      zIndex: '99999',
      background: '#354A5F',
      top: 0,
      width: width > 0 ? `${width}px` : '100%', maxWidth: '100%'
    }}
  >
    <PageLockToggle isLocked={leftLock.isLocked} isLoading={leftLock.isLoading} onToggle={leftLock.toggleLock} />

    {checkMarkSuccess ?
      <Flex px={3} alignItems="center" py={1} sx={{
        borderRadius: '30px',
        background: '#E0FFEA'
      }}
        ref={el => {
          if (el) {
            setTimeout(() => {
              setCheckMarkSuccess(null);
            }, 3000);
          }
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M16.6654 5L7.4987 14.1667L3.33203 10" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" stroke="#00611F" />
        </svg>
        <Text fontFamily="Poppins" fontWeight={500} letterSpacing="-0.32px" lineHeight="150%" ml={1} fontSize="16px" color="#00611F">
          Changes saved
        </Text>
      </Flex>
      : <Flex alignItems="center" sx={{ gap: '16px' }}>
        <Flex
          alignItems="center"
          sx={{ gap: '4px', cursor: canUndo ? 'pointer' : 'not-allowed', opacity: canUndo ? 1 : 0.4 }}
          onClick={canUndo ? onUndo : undefined}
        >
          <MdUndo size={18} color="#FFFFFF" />
          <Text fontFamily="Poppins" fontSize="14px" color="#FFFFFF">Undo</Text>
        </Flex>
        <Flex
          alignItems="center"
          sx={{ gap: '4px', cursor: canRedo ? 'pointer' : 'not-allowed', opacity: canRedo ? 1 : 0.4 }}
          onClick={canRedo ? onRedo : undefined}
        >
          <MdRedo size={18} color="#FFFFFF" />
          <Text fontFamily="Poppins" fontSize="14px" color="#FFFFFF">Redo</Text>
        </Flex>
        <Button
          type="button"
          variant="primary"
          disabled={!canSave || isSaving}
          onClick={onSaveChanges}
          height={36}
          px={3}
          fontFamily="Poppins"
          fontWeight={500}
          fontSize="14px"
          css="border-radius: 20px;    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;"
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </Button>
      </Flex>}

    <PageLockToggle isLocked={rightLock.isLocked} isLoading={rightLock.isLoading} onToggle={rightLock.toggleLock} />
  </Flex >
);

const lockShape = PropTypes.shape({
  isLocked: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  toggleLock: PropTypes.func.isRequired,
});

BookEditorTopBar.propTypes = {
  leftLock: lockShape.isRequired,
  rightLock: lockShape.isRequired,
  canUndo: PropTypes.bool.isRequired,
  canRedo: PropTypes.bool.isRequired,
  onUndo: PropTypes.func.isRequired,
  onRedo: PropTypes.func.isRequired,
  canSave: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  onSaveChanges: PropTypes.func.isRequired,
  width: PropTypes.number,
};

BookEditorTopBar.defaultProps = {
  isSaving: false,
  width: 0,
};

export default BookEditorTopBar;
